import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "../services/AxiosInterceptor";
import { Eye, EyeOff, Mail, Lock, LogIn, UserPlus } from 'lucide-react';

const CustomerLogin = ({ setLogin }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const showError = (msg) => {
    setPopupMessage(msg);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3000);
  };

  const handleSubmit1 = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await Axios.post("api/login_cx", {
        email: formData.email,
        password: formData.password,
      });

      const result = response?.data;

      if (!result || !result.token) {
        showError("Login failed. Please check your email or password.");
        setLoading(false);
        return;
      }

      localStorage.setItem("token", result.token);
      localStorage.setItem("email", formData.email);
      localStorage.setItem("role", "Customer");
      localStorage.setItem("userId", result.userId);
      localStorage.setItem("mobile", result.mobile_no);
      localStorage.setItem("Username", result.name);

      const redirectPath = localStorage.getItem("redirectAfterLogin") || "/";
      localStorage.removeItem("redirectAfterLogin");

      setLoading(false);
      navigate(redirectPath);

    } catch (error) {
      console.log("LOGIN ERROR:", error);
      setLoading(false);

      if (error.response?.status === 404 || error.response?.status === 401) {
        showError("Invalid email or password");
      } else {
        showError("Something went wrong. Please try again.");
      }
    }
  };

  const handleclick = () => {
    setLogin(false);
  };

  return (
    <>
      {showPopup && (
        <div className="fixed bottom-6 right-6 max-w-sm px-6 py-4 text-white bg-gradient-to-r from-red-500 to-pink-600 rounded-xl shadow-2xl text-sm font-medium z-[999999999] animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            {popupMessage}
          </div>
        </div>
      )}

      <div className="min-h-screen w-full bg-gradient-to-br from-orange-50 via-white to-blue-50 flex items-center justify-center p-4">
        
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {/* Left Side - Branding */}
          <div className="hidden lg:flex relative bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 p-8 flex-col justify-center overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10 text-center">
              <h2 className="text-4xl font-bold text-white mb-4">Welcome to<br />Yogamatic</h2>
              <p className="text-orange-100 text-lg mb-8">Transform your life through yoga</p>
              
              <div className="rounded-2xl overflow-hidden shadow-2xl mb-8">
                <img src="/heroYogaPage.jpg" alt="Yoga" className="w-full h-64 object-cover opacity-90"/>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-white">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm">Expert yoga instructors</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm">Personalized sessions</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm">Flexible schedules</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10 bg-white">
            
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back!</h1>
              <p className="text-gray-500">Login to continue your yoga journey</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail size={16} className="text-orange-500" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleSubmit1}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-4 focus:ring-orange-100 text-gray-800"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} className="text-orange-500" />
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleSubmit1}
                    className="w-full px-4 py-2.5 pr-12 border-2 border-gray-200 rounded-xl outline-none transition-all focus:border-orange-400 focus:ring-4 focus:ring-orange-100 text-gray-800"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <div className="text-right">
                  <a href="/forgot_password" className="text-sm text-orange-500 hover:underline">
                    Forgot Password?
                  </a>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Logging in...
                  </>
                ) : (
                  <>
                    <LogIn size={20} />
                    Login
                  </>
                )}
              </button>
            </form>

            <div className="flex items-center gap-4 my-5">
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="text-sm text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>

            <button
              onClick={handleclick}
              className="w-full py-2.5 border-2 border-gray-200 hover:border-orange-300 text-gray-700 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 hover:bg-orange-50"
            >
              <UserPlus size={20} />
              Create New Account
            </button>

            <div className="lg:hidden mt-6 text-center">
              <p className="text-xs text-gray-400">© 2024 Yogamatic. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </>
  );
};

export default CustomerLogin;
